import { LOAD_REQUEST_TIMEOUT, LOAD_TIMEOUT, METADATA_TYPE_PLAYLIST } from '../constants';
import EventTarget from './event-target';
import fileExtension from '../../utils/file-extension';
import isArray from './is-array';
import isString from './is-string';
import Logger from '../../utils/logger';
import { createPlaylist, createPlaylistItem } from './models';
import parseRss from './parse-rss';
import isObject from './is-object';
import { isAudioExtension } from './mime-types';
import ajax from '../../utils/ajax';

class SourceLoaderError extends Error {
  constructor (message, code, isFatal = true) {
    super(message);

    Object.defineProperty(this, 'code', {
      get () {
        return code;
      }
    });

    Object.defineProperty(this, 'fatal', {
      get () {
        return isFatal;
      }
    });
  }
}

class ParseError extends SourceLoaderError {
  constructor () {
    super('Failed to parse playlist data', 'parse');
  }
}

class NetworkError extends SourceLoaderError {
  constructor () {
    super('Network error while loading playlist url', 'network');
  }
}

class TimeoutError extends SourceLoaderError {
  constructor () {
    super('Timeout while loading playlist URL', 'timeout');
  }
}

class AbortError extends SourceLoaderError {
  constructor () {
    super('Playlist loading has been cancelled by the user', 'abort', false);
  }
}

class UnrecognizedSourceError extends SourceLoaderError {
  constructor () {
    super('Unrecognized playlist source', 'unrecognized');
  }
}

export default class SourceLoader extends EventTarget {
  constructor (player, options = {}) {
    super();

    this.player = player;
    this.pending = null;
    this.logger = options.logger || Logger;
  }

  /**
   * Loads and creates playlists from an input source
   * @param {string|array|object} source  The source to be loaded
   * @param {object}              options Additonal options used when loading the source
   * @return {Promise}
   */
  load (source, options = {}) {
    if (this.pending) {
      const next = _ => {
        return this.load(source, options);
      };
      return this.pending.then(next, next);
    }

    this.trigger('loading');

    const deferred = {};
    const promise = this.pending = new Promise((resolve, reject) => {
      deferred.resolve = resolve;
      deferred.reject = reject;
    });

    // Helper function to build and resolve a source
    const resolveSource = source => {
      source = createPlaylist(source);

      this.logger.debug('SourceLoader: Source has been loaded', source);

      this.trigger('loaded');
      deferred.resolve(source);
    };

    // Reject the promise if any errors occur during load
    const rejectError = error => {
      if (error.code === 'abort') {
        this.logger.warn('SourceLoader: Playlist loading has been cancelled by the user');
        this.trigger('cancel');
      } else if (error.code === 'timeout') {
        this.logger.warn('SourceLoader: Timeout while loading playlist URL', {
          source, error
        });
        this.trigger('timeout');
      } else if (error.code === 'network') {
        this.logger.warn('SourceLoader: Network error while loading playlist URL', {
          source, error
        });
        this.trigger('error');
      } else if (error.code === 'parse') {
        this.logger.warn('SourceLoader: Failed to parse playlist URL response');
        this.trigger('error');
      } else {
        this.logger.warn('SourceLoader: Unrecognized error while loading playlist URL');
        this.trigger('error');
      }

      deferred.reject(error);
    };

    // Create a cancel function which will be added to the promise to easily cancel the load
    promise.cancel = function () {
      rejectError(new AbortError());
    };

    // Unset the pending property whether the load succeeds or not
    promise.then(_ => {
      this.pending = null;
    }, _ => {
      this.pending = null;
    });

    // Attempt to load the source
    try {
      if (isString(source)) {
        this.loadFromString(source, options)
          .then(resolveSource, rejectError);
      } else if (isArray(source)) {
        resolveSource(source);
      } else if (isObject(source)) {
        resolveSource(source);
      } else {
        rejectError(new UnrecognizedSourceError());
      }
    } catch (err) {
      // If anything throws above then the load promise is rejected with the unknown error message
      rejectError(err);
    }

    return promise;
  }

  /**
   * Cancel the pending load
   */
  cancel () {
    if (this.pending) {
      this.pending.cancel();
      this.pending = false;
    }
  }

  /**
   * Load a playback source from a string
   *
   * @param {string} source
   * @param {object} options
   *
   * @return {Promise}
   */
  loadFromString (source, options) {
    const ext = fileExtension(source);

    // Load RSS feeds if the file extension or input_type option match
    // eg. load('feed.rss') or load('feed', { input_type : 'rss' })
    if ((options && options.input_type === SourceLoader.TYPE_RSS) || ext === 'rss') {
      return this.loadRss(source, options);
    }

    // Load JSON from remote if the file extension or input_type option match
    // eg. load('endpoint.json') or load('endpoint', { input_type : 'playlist' }) or load('endpoint')
    if (
      (options && options.input_type === SourceLoader.TYPE_PLAYLIST) ||
      (ext === 'json' || !isAudioExtension(ext))
    ) {
      return this.loadJson(source, options);
    }

    this.logger.debug('SourceLoader: Loading direct source', source);

    // create a generic playlist item
    const playlistItem = createPlaylistItem(source);

    // fill in the content type from the input_type option if necessary
    if ((options && options.input_type) && playlistItem.metadata.contentType !== options.input_type) {
      playlistItem.metadata.contentType = options.input_type;
    }

    return Promise.resolve(playlistItem);
  }

  /**
   * Load source from a url
   *
   * @param {string} url     The url to be loaded
   * @param {object} options Additional options
   *
   * @return {Promise}
   */
  loadUrl (url, options) {
    const self = this;
    this.logger.debug(`SourceLoader: Loading URL: ${url}`);

    const sourceUrl = this.player.computed.replace(url, encodeURIComponent, options);
    return new Promise(function sourceLoader_loadUrl_Promise (resolve, reject) {
      ajax(sourceUrl, {
        timeout: LOAD_REQUEST_TIMEOUT
      }).then(function ajax_onload (response) {
        if (!response.ok) {
          self.logger.debug(`SourceLoader: URL returned error status code [${response.status}]`);
          return reject(new NetworkError());
        }
        self.logger.debug('SourceLoader: URL loaded', response);
        resolve(response.text());
      }, function ajax_onerror (err) {
        if (err.name === 'Abort') {
          return reject(new AbortError());
        }

        if (err.name === 'Timeout') {
          return reject(new TimeoutError());
        }

        return reject(new NetworkError());
      });
    });
  }

  /**
   * Load source from an RSS feed
   *
   * @param {string} url     Url to an RSS feed
   * @param {object} options Additional option
   *
   * @return {Promise}
   */
  loadRss (url, options) {
    this.logger.debug(`SourceLoader: Loading RSS feed: ${url}`);

    return new Promise((resolve, reject) => {
      this.loadUrl(url, options)
        .then((response) => {
          // Parse the RSS response in to a playlist
          parseRss(response, (err, source) => {
            if (err) {
              this.logger.error(`SourceLoader: Error parsing RSS feed: ${err.message}`);
              reject(new ParseError());
            } else {
              source.metadata.url = url;
              resolve(source);
            }
          });
        }, reject);
    });
  }

  /**
   * Load source from a JSON object at the url
   *
   * @param {string} url     Url of a JSON encoded resource
   * @param {object} options Additional options
   *
   * @return {Promise}
   */
  loadJson (url, options) {
    const self = this;
    this.logger.debug(`SourceLoader: Loading JSON url: ${url}`);

    return new Promise((resolve, reject) => {
      this.loadUrl(url, options)
        .then(function loadJsonSuccess (response) {
          // Create playlist from response
          self.logger.debug('SourceLoader: Parsing JSON object');

          try {
            let data = JSON.parse(response);

            if (isArray(data)) {
              data = {
                metadata: {
                  contentType: METADATA_TYPE_PLAYLIST,
                  url: url
                },
                items: data
              };
            } else if (isObject(data) && data.metadata && data.metadata.contentType === METADATA_TYPE_PLAYLIST && !data.metadata.url) {
              data.metadata.url = url;
            }

            resolve(data);
          } catch (e) {
            reject(new ParseError());
          }
        }, reject);
    });
  }
}

SourceLoader.TYPE_PLAYLIST = 'playlist';
SourceLoader.TYPE_STREAM = 'stream';
SourceLoader.TYPE_PODCAST = 'podcast';
SourceLoader.TYPE_RSS = 'rss';
